/**
 * DviStageEstimate - Stage 5: Estimate draft workflow
 * 
 * Build, review, and apply estimate drafts.
 */

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  FileText, 
  Loader2, 
  Plus,
  CheckCircle,
  Clock,
  XCircle,
  ArrowRight
} from 'lucide-react';
import { EstimateDraftPanel } from '../EstimateDraftPanel';
import type { EstimateItem } from '@/hooks/useDviEstimate';

interface DviStageEstimateProps {
  currentDraft: {
    id: string;
    status: string;
    review_status: string;
    total_labor_minutes: number;
    total_items: number;
    created_at: string;
  } | null;
  draftItems: EstimateItem[];
  totalLaborMinutes: number;
  pendingReviewItems: EstimateItem[];
  approvedItems: EstimateItem[];
  rejectedItems: EstimateItem[];
  onBuildDraft: (filter?: string) => void;
  onApproveItems: (ids: string[]) => void;
  onRejectItems: (ids: string[], reason?: string) => void;
  onApplyItems: (ids: string[]) => void;
  onApplyAllApproved: () => void;
  isBuildingDraft: boolean;
  isReviewing: boolean;
  isApplying: boolean;
  canAddToTicket: boolean;
  canApprove: boolean;
  hasAnalyzedGroups: boolean;
  isLoading: boolean;
  onJobsAdded?: () => void;
}

const REVIEW_STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  pending_review: { label: 'Pending Review', className: 'bg-yellow-500/10 text-yellow-600' },
  approved: { label: 'Approved', className: 'bg-green-500/10 text-green-600' },
  rejected: { label: 'Rejected', className: 'bg-red-500/10 text-red-600' },
  mixed: { label: 'Mixed', className: 'bg-blue-500/10 text-blue-600' },
};

export function DviStageEstimate({
  currentDraft,
  draftItems,
  totalLaborMinutes,
  pendingReviewItems,
  approvedItems,
  rejectedItems,
  onBuildDraft,
  onApproveItems,
  onRejectItems,
  onApplyItems,
  onApplyAllApproved,
  isBuildingDraft,
  isReviewing,
  isApplying,
  canAddToTicket,
  canApprove,
  hasAnalyzedGroups,
  isLoading,
  onJobsAdded,
}: DviStageEstimateProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!currentDraft) {
    return (
      <div className="text-center py-8 space-y-4">
        <FileText className="h-12 w-12 text-muted-foreground mx-auto" />
        <div>
          <p className="text-sm font-medium">No Estimate Draft</p>
          <p className="text-xs text-muted-foreground">
            {hasAnalyzedGroups 
              ? 'Build an estimate from analyzed finding groups' 
              : 'Analyze finding groups first to build an estimate'}
          </p>
        </div>
        {hasAnalyzedGroups && canAddToTicket && (
          <Button onClick={() => onBuildDraft('yellow_red')} disabled={isBuildingDraft} className="gap-2">
            {isBuildingDraft ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Plus className="h-4 w-4" />
            )}
            Build Estimate
          </Button>
        )}
      </div>
    );
  }

  const reviewConfig = REVIEW_STATUS_CONFIG[currentDraft.review_status] || REVIEW_STATUS_CONFIG.pending_review;

  return (
    <div className="space-y-4">
      {/* Draft Summary Card */}
      <Card>
        <CardContent className="p-3 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Estimate Draft</span>
              <Badge variant="outline" className={reviewConfig.className}>
                {reviewConfig.label}
              </Badge>
            </div>
            {canAddToTicket && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onBuildDraft('yellow_red')}
                disabled={isBuildingDraft}
                className="gap-1.5 h-7"
              >
                {isBuildingDraft ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : null}
                Rebuild
              </Button>
            )}
          </div>
          
          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            <span>{currentDraft.total_items} items</span>
            <span>{totalLaborMinutes} min total</span>
            <span>Created {new Date(currentDraft.created_at).toLocaleDateString()}</span>
          </div>

          {/* Review status counts */}
          <div className="flex items-center gap-2 flex-wrap pt-1">
            {pendingReviewItems.length > 0 && (
              <Badge variant="outline" className="gap-1 text-xs bg-yellow-500/10 text-yellow-600">
                <Clock className="h-3 w-3" />
                {pendingReviewItems.length} pending
              </Badge>
            )}
            {approvedItems.length > 0 && (
              <Badge variant="outline" className="gap-1 text-xs bg-green-500/10 text-green-600">
                <CheckCircle className="h-3 w-3" />
                {approvedItems.length} approved
              </Badge>
            )}
            {rejectedItems.length > 0 && (
              <Badge variant="outline" className="gap-1 text-xs bg-red-500/10 text-red-600">
                <XCircle className="h-3 w-3" />
                {rejectedItems.length} rejected
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Estimate Items Panel */}
      <EstimateDraftPanel
        items={draftItems}
        totalLaborMinutes={totalLaborMinutes}
        onApplySelected={onApplyItems}
        onApplyAll={onApplyAllApproved}
        isApplying={isApplying}
        onApproveItems={onApproveItems}
        onRejectItems={onRejectItems}
        isReviewing={isReviewing}
        onJobsAdded={onJobsAdded}
      />
    </div>
  );
}
